/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useState, useCallback } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

import { useDispatch, useSelector } from "react-redux";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { ColumnList } from "components/ColumnList";
import {
  tableSelector,
  setOrderedColumns,
  setSomaticOrderedColumns,
  setTestInfoOrderedColumns,
} from "store/slices/tableSlice";
import DateFilter from "components/DateFilter/DateFilter";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted = false,
  isDraggable = false,
  noEndBorder,
  canHideColumns = false,
  canFilterByDate = false,
  activeRow = null,
  columnNamespace = "main",
}) {
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const { orderedColumns, somaticOrderedColumns, testInfoOrderedColumns } =
    useSelector(tableSelector);
  const columnOrder =
    columnNamespace === "somatic"
      ? somaticOrderedColumns
      : columnNamespace === "testInfo"
      ? testInfoOrderedColumns
      : orderedColumns;
  const setColumnOrder =
    columnNamespace === "somatic"
      ? setSomaticOrderedColumns
      : columnNamespace === "testInfo"
      ? setTestInfoOrderedColumns
      : setOrderedColumns;
  const dispatch = useDispatch();

  const reorderColumn = useCallback(
    (draggedColumnId, targetColumnId) => {
      const newColumnOrder = [...columnOrder];
      const draggedIndex = newColumnOrder.findIndex(
        (col) => col.Header.toLowerCase() === draggedColumnId.toLowerCase()
      );
      const targetIndex = newColumnOrder.findIndex(
        (col) => col.Header.toLowerCase() === targetColumnId.toLowerCase()
      );

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const draggedColumn = newColumnOrder[draggedIndex];
        newColumnOrder.splice(draggedIndex, 1);
        newColumnOrder.splice(targetIndex, 0, draggedColumn);
        dispatch(setColumnOrder(newColumnOrder));
      }
    },
    [columnOrder]
  );

  const visibleColumns = useMemo(
    () =>
      columnOrder
        .filter((co) => !co.isHidden)
        .map((co) =>
          columns.find((col) => {
            return col.Header.toLowerCase() === co.Header.toLowerCase();
          })
        )
        .filter((col) => col),
    [columns, columnOrder]
  );

  const handleDragStart = (e, columnId) => {
    const column = headers.find((col) => col.id === columnId);
    if (column && column.isVisible) {
      e.dataTransfer.setData("columnId", columnId);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const tableInstance = useTable(
    {
      columns: canHideColumns ? visibleColumns : columns,
      data,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    columns: headers,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    const draggedColumnId = e.dataTransfer.getData("columnId");
    const draggedColumn = headers.find((col) => col.id === draggedColumnId);
    const targetColumn = headers.find((col) => col.id === targetColumnId);

    if (
      draggedColumn &&
      targetColumn &&
      draggedColumn.isVisible &&
      targetColumn.isVisible &&
      draggedColumnId !== targetColumnId
    ) {
      reorderColumn(draggedColumnId, targetColumnId);
    }
  };

  const setEntriesPerPage = (value) => setPageSize(value);

  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  const customizedPageOptions = pageOptions.map((option) => option + 1);

  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    let sortedValue;

    if (!column.canSort) {
      return false;
    }

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDBox display="flex" alignItems="center">
          {canHideColumns && <ColumnList columnNamespace={columnNamespace} />}
          {canFilterByDate && <DateFilter />}
        </MDBox>
        {entriesPerPage && (
          <MDBox display="flex" alignItems="center">
            <Autocomplete
              disableClearable
              value={pageSize.toString()}
              options={entries}
              onChange={(event, newValue) => {
                setEntriesPerPage(parseInt(newValue, 10));
              }}
              size="small"
              sx={{ width: "5rem" }}
              renderInput={(params) => <MDInput {...params} />}
            />
            <MDTypography variant="caption" color="secondary">
              &nbsp;&nbsp;entries per page
            </MDTypography>
          </MDBox>
        )}
        {canSearch && (
          <MDBox width="12rem" ml="auto">
            <MDInput
              placeholder="Search..."
              value={search}
              size="small"
              fullWidth
              onChange={({ currentTarget }) => {
                setSearch(search);
                onSearchChange(currentTarget.value);
              }}
            />
          </MDBox>
        )}
      </MDBox>
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(
                (column, idx) =>
                  column.isVisible && (
                    <DataTableHeadCell
                      key={idx}
                      {...column.getHeaderProps(
                        column.canSort && isSorted ? column.getSortByToggleProps() : undefined
                      )}
                      width={column.width ? column.width : "auto"}
                      align={column.align ? column.align : "left"}
                      sorted={setSortedValue(column)}
                      draggable={isDraggable}
                      onDragStart={(e) => handleDragStart(e, column.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, column.id)}
                      borderLeft={column.borderLeft}
                      borderRight={column.borderRight}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  )
              )}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow
                key={key}
                {...row.getRowProps()}
                onClick={() => row.original.onClick && row.original.onClick()}
                sx={{
                  cursor: row.original.onClick ? "pointer" : "default",
                  backgroundColor:
                    activeRow === row.original.deviceId ? "rgba(0, 0, 0, 0.04)" : "inherit",
                  "&:hover": {
                    backgroundColor:
                      activeRow === row.original.deviceId
                        ? "rgba(0, 0, 0, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                hover={!!row.original.onClick}
              >
                {row.cells.map(
                  (cell, idx) =>
                    cell.column.isVisible && (
                      <DataTableBodyCell
                        key={idx}
                        noBorder={noEndBorder && rows.length - 1 === key}
                        align={cell.column.align ? cell.column.align : "left"}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </DataTableBodyCell>
                    )
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  canHideColumns: PropTypes.bool,
  canFilterByDate: PropTypes.bool,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  isDraggable: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  activeRow: PropTypes.any,
  columnNamespace: PropTypes.string,
};

export default DataTable;
